import { Box, Typography, useMediaQuery } from '@mui/material';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SlotCounter from 'react-slot-counter';

import {
  Button,
  LocalModal,
  ModalContentContainer,
  ToggleableOption,
} from '@/components';
import { ComplianceTypes } from '@/constants';
import { checkmarkRaised } from '@/images';
import { AnalyticsService, EVENTS } from '@/services';
import {
  selectIsPinnedFieldsOfStudyDismissed,
  selectUserCompliance,
  useAppSelector,
  useFetchFieldsOfStudyQuery,
  useFetchUserUIStatesQuery,
  userPreferencesActions,
  useUpdateUserUIStateMutation,
} from '@/store';
import { appStyles } from '@/theme';
import { Certificate } from '@/types';

const MAX_FOS_COUNT = 5;
const MAX_FOS_COUNT_STRING = 'five';

export const PinnedFieldsOfStudy = ({
  credits,
}: {
  credits: Certificate[];
}) => {
  const compliance = useSelector(selectUserCompliance);

  if (compliance !== ComplianceTypes.CPE) return;

  return <PinnedFieldsOfStudyContent credits={credits} />;
};

const DEFAULT_FIELDS = [
  'ACCOUNTING',
  'AUDITING',
  'TAXES',
  'BEHAVIORAL_ETHICS',
  'REGULATORY_ETHICS',
];

const PinnedFieldsOfStudyContent = ({
  credits,
}: {
  credits: Certificate[];
}) => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const dispatch = useDispatch();

  const { data: userUIStates, isLoading: isPinnedFieldsLoading } =
    useFetchUserUIStatesQuery();
  const { data: fieldsOfStudy, isLoading: isFieldsOfStudyLoading } =
    useFetchFieldsOfStudyQuery();

  const [updateUserUIState, { isLoading: isPinnedFieldsUpdating }] =
    useUpdateUserUIStateMutation();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [tempPinnedFields, setTempPinnedFields] = useState<string[]>([]);

  const pinnedFields = useMemo(() => {
    // Create list of pinned fields with total credits
    const fields = userUIStates?.pinnedFieldsOfStudy ?? DEFAULT_FIELDS;
    const fieldsTotals = credits.reduce(
      (acc, credit) => {
        const fos = credit.fieldsOfStudy?.[0]?.fieldOfStudyId;
        if (fos && fields.includes(fos)) {
          if (!acc[fos]) acc[fos] = 0;
          acc[fos] += credit.credit ?? 0;
        }
        return acc;
      },
      {} as Record<string, number>,
    );

    return fields.map(field => ({
      key: field,
      total: Math.round((fieldsTotals[field] ?? 0) * 100) / 100,
    }));
  }, [userUIStates, credits]);

  const sortedFieldsOfStudy = useMemo(() => {
    if (!fieldsOfStudy) return [];
    return Object.entries(fieldsOfStudy).sort((a, b) => {
      const aIsPinned = pinnedFields.some(f => f.key === a[0]);
      const bIsPinned = pinnedFields.some(f => f.key === b[0]);
      if ((aIsPinned && bIsPinned) || (!aIsPinned && !bIsPinned))
        return a[1].localeCompare(b[1]);
      if (aIsPinned) return -1;
      return 1;
    });
  }, [fieldsOfStudy, pinnedFields]);

  const isNotificationDismissed = useAppSelector(
    selectIsPinnedFieldsOfStudyDismissed,
  );

  // Hide the feature notification is it's been dismissed or the user has previously pinned fields
  const hideNotification =
    isNotificationDismissed || userUIStates?.pinnedFieldsOfStudy?.length;

  if (isPinnedFieldsLoading || isFieldsOfStudyLoading || !fieldsOfStudy) return;

  const onOpenModal = () => {
    setTempPinnedFields(pinnedFields.map(field => field.key));
    setIsEditModalOpen(true);
  };

  const onSubmit = async () => {
    await updateUserUIState({
      key: 'pinnedFieldsOfStudy',
      value: tempPinnedFields.sort((a, b) => a.localeCompare(b)),
    });
    setIsEditModalOpen(false);
    AnalyticsService.logEvent(EVENTS.TRACKER_EVENTS.PINNED_FOS_EDIT, {
      pinned_fields: tempPinnedFields,
    });
  };

  const onDismiss = () => {
    dispatch(userPreferencesActions.dismissPinnedFieldsOfStudy());
    AnalyticsService.logEvent(EVENTS.TRACKER_EVENTS.PINNED_FOS_DISMISS);
  };

  return (
    <Box sx={{ mt: 4 }}>
      {hideNotification ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
          <Typography variant="body1SemiBold">
            Credits by Field of Study
          </Typography>
          <Button
            label={isMobile ? 'Edit' : 'Edit Fields of Study'}
            variant="secondary"
            fullWidth={false}
            onClick={onOpenModal}
          />
        </Box>
      ) : (
        <Box
          sx={{
            p: 3,
            borderRadius: 2,
            border: 1,
            borderColor: 'secondary.main',
            backgroundColor: 'purple25',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1.5,
          }}>
          <Box
            component={'img'}
            src={checkmarkRaised}
            alt="Checkmark"
            sx={{ width: 57, height: 57, display: { xs: 'none', sm: 'block' } }}
          />
          <Box>
            <Typography variant="body1SemiBold">
              Track your credits by specific Fields of Study
            </Typography>
            <Typography variant="body3" sx={{ mt: 0.5 }}>
              Choose up to {MAX_FOS_COUNT_STRING} Fields of Study and easily
              track them below.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
            <Button variant="plain" label="Maybe Later" onClick={onDismiss} />
            <Button variant="primary" label="Customize" onClick={onOpenModal} />
          </Box>
        </Box>
      )}
      {pinnedFields.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            width: '100%',
            mt: 2,
            flexWrap: 'wrap',
          }}>
          {pinnedFields.map((field, index) => (
            <Box
              key={index}
              sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: 'sand200',
                borderRadius: 2,
                backgroundColor: 'sand50',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                flex: 1,
                width: { xs: '50%', md: 'auto' },
                maxWidth: {
                  xs: 'none',
                  md: 300,
                },
              }}>
              <Typography variant="caption1Bold">
                {fieldsOfStudy[field.key].toUpperCase()}
              </Typography>
              <Typography variant="hero3">
                <SlotCounter startValue={0} value={field.total} />
              </Typography>
              <Typography variant="caption1" color="text.light">
                credits
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      <LocalModal
        sx={appStyles.scrollbar}
        open={isEditModalOpen}
        name="Tracker - Pin Fields of Study"
        onClose={() => setIsEditModalOpen(false)}>
        <Box sx={{ width: { xs: 'auto', sm: 500 } }}>
          <ModalContentContainer
            stretch
            onModalClose={() => setIsEditModalOpen(false)}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 1, sm: 3 },
              }}>
              <Typography variant="h2">Customize Fields of Study</Typography>
              <Typography variant="body3">
                Select and pin up to {MAX_FOS_COUNT_STRING} Fields of Study on
                your tracker page.
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  overflowY: 'scroll',
                  maxHeight: 300,
                  pr: 1,
                }}>
                {sortedFieldsOfStudy.map(([key, label], index) => (
                  <ToggleableOption
                    key={index}
                    label={label}
                    onClick={() => {
                      const newPinnedList = tempPinnedFields.includes(key)
                        ? tempPinnedFields.filter(field => field !== key)
                        : [...tempPinnedFields, key];
                      setTempPinnedFields(newPinnedList);
                    }}
                    isSelected={tempPinnedFields.includes(key)}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ mr: 'auto' }}>
                  <Typography
                    variant="body2SemiBold"
                    sx={{
                      color:
                        tempPinnedFields.length <= MAX_FOS_COUNT
                          ? 'secondary.main'
                          : 'red',
                    }}>
                    {tempPinnedFields.length} of {MAX_FOS_COUNT} selected
                  </Typography>
                  {tempPinnedFields.length > MAX_FOS_COUNT && (
                    <Typography variant="body3" color="text.main">
                      You can select up to {MAX_FOS_COUNT_STRING} Fields of
                      Study
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="secondary"
                  fullWidth={false}
                  sx={{ mr: 1, ml: 2 }}
                  label="Cancel"
                  onClick={() => setIsEditModalOpen(false)}
                />
                <Button
                  variant="primary"
                  fullWidth={false}
                  label="Save"
                  isLoading={isPinnedFieldsUpdating}
                  disabled={tempPinnedFields.length > MAX_FOS_COUNT}
                  onClick={onSubmit}
                />
              </Box>
            </Box>
          </ModalContentContainer>
        </Box>
      </LocalModal>
    </Box>
  );
};
